import {
  AmendmentField,
  AmendmentWidgetField,
  buildAmendmentFieldId,
  buildAmendmentWidgetId,
  WidgetFieldExtractor,
} from './amendment-field.model';
import { EMR_WIDGET_FIELD_EXTRACTORS } from './amendment-fields.emr';
import { asArray, asRecord, normalizeValue, readKey } from './amendment-value.util';

/**
 * Widgets whose answers live in `properties.columns[i].value.value`, one field per column.
 */
const extractColumnFields: WidgetFieldExtractor = (properties) =>
  asArray(properties['columns']).map((column, columnIndex) => ({
    fieldKey: `c${columnIndex}`,
    value: normalizeValue(readKey(readKey(column, 'value'), 'value')),
  }));

/**
 * Widgets whose single answer lives in `properties.value.value`.
 */
const extractSingleValue: WidgetFieldExtractor = (properties) => [
  { fieldKey: null, value: normalizeValue(readKey(properties['value'], 'value')) },
];

const extractCheckboxComposite: WidgetFieldExtractor = (properties) => [
  {
    fieldKey: null,
    value: asArray(readKey(properties['value'], 'value')).map((item) =>
      isChecked(readKey(readKey(item, 'properties'), 'value')),
    ),
  },
];

const extractMatrix: WidgetFieldExtractor = (properties) => [
  {
    fieldKey: null,
    value: asArray(readKey(properties['rows'], 'value')).map((row) =>
      matrixRowSelection(readKey(row, 'properties')),
    ),
  },
];

/**
 * Extractors by widget name. Media, attachments, signatures and static content are not form
 * fields and have no extractor, so they never produce ids.
 */
const WIDGET_FIELD_EXTRACTORS: Readonly<Record<string, WidgetFieldExtractor>> = {
  'fb-textbox-composite': extractColumnFields,
  'fb-textarea-composite': extractColumnFields,
  'fb-dropdown': extractColumnFields,
  'fb-radio-composite': extractSingleValue,
  'fb-radio-simple': extractSingleValue,
  'fb-checkbox-composite': extractCheckboxComposite,
  'fb-matrix': extractMatrix,
  ...EMR_WIDGET_FIELD_EXTRACTORS,
};

/**
 * Lists the trackable fields of a form with their current values.
 *
 * Works on the live `FormConfig` and on its parsed JSON snapshot alike, so the result can be
 * diffed between the version loaded into the editor and the edited one.
 */
export function extractAmendmentFields(formData: unknown): AmendmentField[] {
  const fields: AmendmentField[] = [];

  asArray(readKey(formData, 'sections')).forEach((section, sectionIndex) => {
    asArray(readKey(section, 'widgets')).forEach((widget, widgetIndex) => {
      const widgetId = buildAmendmentWidgetId(sectionIndex, widgetIndex);

      extractWidgetFields(widget).forEach((field) => {
        fields.push({ id: buildAmendmentFieldId(widgetId, field.fieldKey), value: field.value });
      });
    });
  });

  return fields;
}

function extractWidgetFields(widget: unknown): AmendmentWidgetField[] {
  const name = readKey(widget, 'name');
  const properties = asRecord(readKey(widget, 'properties'));
  const extract = typeof name === 'string' ? WIDGET_FIELD_EXTRACTORS[name] : undefined;

  return extract && properties ? extract(properties) : [];
}

function isChecked(valueProperty: unknown): boolean {
  const value = readKey(valueProperty, 'value');
  return typeof value === 'boolean' ? value : Number(value) > 0;
}

/**
 * The matrix widget rewrites a legacy `{ value }` selection into a plain number when it
 * initializes, so both shapes must resolve to the same comparable value.
 */
function matrixRowSelection(rowProperties: unknown): number {
  const selected = readKey(rowProperties, 'selected');
  const raw = asRecord(selected) ? readKey(selected, 'value') : selected;
  const selection = raw === null || raw === undefined || raw === '' ? -1 : Number(raw);
  return Number.isFinite(selection) ? selection : -1;
}
